/**
 * Declaración de las columnas de la grilla de Móviles, para <GridColumnSelector /> y
 * `useColumnVisibility` (src/commons/grid/).
 *
 * Vive en su propio módulo por lo mismo que `movilesFilterFields.js`: lo consumen el selector
 * (para listar) y la grilla (para decidir qué celda se ve).
 *
 * TRES REGLAS al tocar esta lista:
 *
 * 1. El ORDEN es el del `thead`. Es el orden en que el usuario ve las opciones en el selector,
 *    y que no coincida con la grilla se nota.
 *
 * 2. *** EL `name` DE UNA COLUMNA GATEADA POR CONFIG ES LA CLAVE DE `columnsEnabled`, CON SU
 *    SUFIJO `Enabled` INCLUIDO *** (`unidadEnabled`, `rentingEnabled`...). Se ve raro al lado de
 *    los nombres sueltos (`marca`, `dominio`), pero es deliberado: el mapa que devuelve el hook
 *    se le pasa a MovilesGridRow en la MISMA prop `columnsEnabled` que ya recibía, así las 25
 *    celdas que ya estaban gateadas siguen funcionando sin tocarlas. Cambiar estos nombres
 *    obliga a tocar las dos puntas.
 *
 * 3. El `messageId` es el MISMO del encabezado de la columna. Si el selector dijera algo
 *    distinto de lo que dice el `th`, el usuario no podría relacionarlos.
 *
 * `fixed: true` = no se puede ocultar y no aparece en el selector. Son las columnas SIN TÍTULO
 * (el checkbox de selección masiva, los dos iconos de estado, la celda vacía) y la de acciones:
 * en el selector aparecerían como casillas sin etiqueta, y ocultar las acciones dejaría la fila
 * sin sus botones y sin una forma evidente de recuperarlos.
 */

import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

export const MOVILES_COLUMNAS_STORAGE_KEY = 'MovilesGrid_columnas';

/**
 * Qué columnas EXISTEN para este cliente: los flags de config y los permisos.
 *
 * *** VIVE ACÁ, AL LADO DE LAS COLUMNAS QUE LOS CONSUMEN, Y NO EN LA GRILLA. ***
 * Estaba repartido entre el inicializador del `useState` y un `configColumns()` que corría en un
 * efecto; al unificarlo se perdieron dos flags (`supervisorEnabled`, `temporalEnabled`) que eran
 * los únicos que estaban en el inicializador, y esas dos columnas quedaron apagadas para todos.
 * Juntos y con el test de abajo, ese error no se puede repetir en silencio.
 *
 * *** Y SE CALCULA ANTES DEL PRIMER RENDER, NO EN UN EFECTO. ***
 * `GridFilterPanel` NO SERIALIZA los campos invisibles y aplica su estado al montar
 * (`applyOnMount`): con los flags en false durante el render 0, los filtros gateados no viajan
 * aunque estén guardados. Es todo síncrono, así que no hay razón para diferirlo.
 *
 * @param permisos  `{ disponibilidadListar }`. Se INYECTA en vez de importar `Security` porque
 *                  ese módulo arrastra Firebase y volvería intesteable a este.
 */
export const buildColumnsEnabled = (permisos) => ({
	paisEnabled: ConfigBusiness.get('moviles.pais.grilla') === 'true',
	subRegionEnabled: ConfigBusiness.get('moviles.subRegion.grilla') === 'true',
	editKmEnabled: ConfigBusiness.get('moviles.editaKm.habilitado') === 'true',
	regionEnabled: ConfigBusiness.get('moviles.region.grilla') === 'true',
	unidadEnabled: ConfigBusiness.get('moviles.unidad.grilla') === 'true',
	titularEnabled: ConfigBusiness.get('moviles.titular.grilla') === 'true',
	companiaOrigenEnabled: ConfigBusiness.get('moviles.companiaOrigen.grilla') === 'true',
	centroCostosEnabled: ConfigBusiness.get('moviles.centroCostos.grilla') === 'true',
	cecosDireccionEnabled: ConfigBusiness.get('moviles.cecosDireccion.grilla') === 'true',
	cecosMercadoEnabled: ConfigBusiness.get('moviles.cecosMercado.grilla') === 'true',
	cecosGerenciaEnabled: ConfigBusiness.get('moviles.cecosGerencia.grilla') === 'true',
	cecosAreaEnabled: ConfigBusiness.get('moviles.cecosArea.grilla') === 'true',
	responsable1Enabled: ConfigBusiness.get('moviles.responsable1.grilla') === 'true',
	responsable2Enabled: ConfigBusiness.get('moviles.responsable2.grilla') === 'true',
	chasisEnabled: ConfigBusiness.get('moviles.chasis.grilla') === 'true',
	baseEnabled: ConfigBusiness.get('moviles.base.grilla') === 'true',
	disponibilidadEnabled: ConfigBusiness.get('moviles.clasificador.habilitado') === 'true',
	rutaEnabled: ConfigBusiness.get('moviles.ruta.grilla') === 'true',
	movilReservaEnabled: ConfigBusiness.get('moviles.movil_reserva.habilitado') === 'true',
	rentingEnabled: ConfigBusiness.get('frontend.rentadora.grilla') === 'true',
	transportadoraEnabled: ConfigBusiness.get('moviles.transportadora.grilla') === 'true',
	esReservaEnabled: ConfigBusiness.get('moviles.reserva.grilla') === 'true',
	// Estos dos estaban SOLO en el inicializador del useState, no en configColumns.
	supervisorEnabled: ConfigBusiness.get('moviles.supervisor.grilla') === 'true',
	temporalEnabled: ConfigBusiness.get('moviles.temporal.grilla') === 'true',
	ultimaBipeoEnabled: (permisos || {}).disponibilidadListar === true,
});

/**
 * @param columnsEnabled  los flags de config que calcula MovilesGrid::configColumns
 * @param permissions     para "Actualizar Medidor", que además de flag pide permiso
 */
export const buildMovilesColumns = (columnsEnabled, permissions) => {
	const flags = columnsEnabled || {};
	const permisos = permissions || {};

	return [
		{
			// El checkbox de selección masiva sólo existe con `tickets.generacionMasiva.habilitado`.
			// Es `fixed` (no va al selector) pero SÍ tiene que reflejar si se ve, porque la grilla
			// cuenta las columnas visibles para el `colSpan` de las filas de carga y sin resultados.
			name: 'seleccion',
			fixed: true,
			enabled: ConfigBusiness.get('tickets.generacionMasiva.habilitado') === 'true',
		},
		{ name: 'marca', messageId: 'movilesGrid.rennder.column_marca.label', label: 'Marca' },
		{ name: 'disponibilidadEnabled', enabled: flags.disponibilidadEnabled === true, fixed: true },
		{ name: 'estado', fixed: true },
		{ name: 'unidadEnabled', messageId: 'movilesGrid.rennder.column_unidad.label', label: 'Unidad', enabled: flags.unidadEnabled === true },
		{ name: 'chasisEnabled', messageId: 'movilesGrid.rennder.column_chasis.label', label: 'Chasis', enabled: flags.chasisEnabled === true },
		{ name: 'dominio', messageId: 'movilesGrid.rennder.column_dominio.label', label: 'Dominio' },
		{ name: 'rutaEnabled', messageId: 'Ruta', label: 'Ruta', enabled: flags.rutaEnabled === true },
		{ name: 'movilReservaEnabled', messageId: 'movilesGrid.render.column_movil_reserva.label', label: 'Vehículo Reserva', enabled: flags.movilReservaEnabled === true },
		{ name: 'modelo', messageId: 'movilesGrid.rennder.column_modelo.label', label: 'Modelo' },
		{ name: 'proxTareas', messageId: 'movilesGrid.rennder.column_prox_tareas.label', label: 'Prox. Tareas' },
		{ name: 'tipo', messageId: 'movilesGrid.rennder.column_tipo.label', label: 'Tipo' },
		{ name: 'paisEnabled', messageId: 'movilesGrid.rennder.column_pais.label', label: 'País', enabled: flags.paisEnabled === true },
		{ name: 'regionEnabled', messageId: 'movilesGrid.rennder.column_region.label', label: 'Región', enabled: flags.regionEnabled === true },
		{ name: 'subRegionEnabled', messageId: 'movilesGrid.rennder.column_subRegion.label', label: 'Sub.Región', enabled: flags.subRegionEnabled === true },
		{ name: 'baseEnabled', messageId: 'movilesGrid.rennder.column_base.label', label: 'Base', enabled: flags.baseEnabled === true },
		{ name: 'transportadoraEnabled', messageId: 'movilesGrid.rennder.column_transportadora.label', label: 'Transportadora', enabled: flags.transportadoraEnabled === true },
		{ name: 'rentingEnabled', messageId: 'movilesGrid.rennder.column_rentadora.label', label: 'Rentadora', enabled: flags.rentingEnabled === true },
		{ name: 'titularEnabled', messageId: 'movilesGrid.rennder.column_titular.label', label: 'Titular', enabled: flags.titularEnabled === true },
		{ name: 'funcion', messageId: 'movilesGrid.rennder.column_funcion.label', label: 'Función' },
		{ name: 'companiaOrigenEnabled', messageId: 'movilesGrid.rennder.column_cia_origen.label', label: 'Cía. Origen', enabled: flags.companiaOrigenEnabled === true },
		{ name: 'centroCostosEnabled', messageId: 'movilesGrid.rennder.column_centro_costos.label', label: 'Centro de Costos', enabled: flags.centroCostosEnabled === true },
		{ name: 'cecosDireccionEnabled', messageId: 'movilesGrid.rennder.column_centro_costos_direccion.label', label: 'CC. Dirección', enabled: flags.cecosDireccionEnabled === true },
		{ name: 'cecosMercadoEnabled', messageId: 'movilesGrid.rennder.column_centro_costos_mercado.label', label: 'CC. Mercado', enabled: flags.cecosMercadoEnabled === true },
		{ name: 'cecosGerenciaEnabled', messageId: 'movilesGrid.rennder.column_centro_costos_gerencia.label', label: 'CC. Gerencia', enabled: flags.cecosGerenciaEnabled === true },
		{ name: 'cecosAreaEnabled', messageId: 'movilesGrid.rennder.column_centro_costos_area.label', label: 'CC. Area', enabled: flags.cecosAreaEnabled === true },
		{ name: 'responsable1Enabled', messageId: 'movilesGrid.rennder.column_responsable1.label', label: 'Responsable 1', enabled: flags.responsable1Enabled === true },
		{ name: 'responsable2Enabled', messageId: 'movilesGrid.rennder.column_responsable2.label', label: 'Responsable 2', enabled: flags.responsable2Enabled === true },
		{ name: 'supervisorEnabled', messageId: 'movilesGrid.render.column_supervisor.label', label: 'Supervisor', enabled: flags.supervisorEnabled === true },
		{ name: 'temporalEnabled', messageId: 'movilesGrid.render.column_temporal.label', label: 'Temporal', enabled: flags.temporalEnabled === true },
		{ name: 'columnaVacia', fixed: true },
		{ name: 'medidor', messageId: 'movilesGrid.rennder.column_medidor.label', label: 'Medidor' },
		{ name: 'esReservaEnabled', messageId: 'movilesGrid.rennder.column_es_reserva.label', label: '¿Es reserva?', enabled: flags.esReservaEnabled === true },
		{
			// Además del flag pide permiso: sin él la columna no existe para este usuario, así que
			// tampoco tiene sentido ofrecerla en el selector.
			//
			// *** SE LLAMA `editKmEnabled` Y NO `actualizarMedidor` POR LA REGLA 2 ***: el header y la
			// celda leen `columnsEnabled.editKmEnabled`. Con el nombre "lindo" el mapa no tenía esa
			// clave, el `show` quedaba en undefined y la columna NO SE RENDERIZABA NUNCA — además de
			// contarse para el colSpan de las filas de carga y sin resultados.
			name: 'editKmEnabled',
			messageId: 'movilesGrid.rennder.column_update_km.label',
			label: 'Actualizar Medidor',
			enabled: flags.editKmEnabled === true && permisos.canModifyKM === true,
		},
		{ name: 'ultimaBipeoEnabled', messageId: 'movilesGrid.render.column_ultima_bipeo.label', label: 'Último bipeo', enabled: flags.ultimaBipeoEnabled === true },
		{ name: 'acciones', fixed: true },
	];
};
